import React, { useMemo, useState } from "react";
import { Row, Col, Card, CardBody, CardHeader, Table, Nav, NavItem, NavLink } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import "moment/locale/es";
import { formatCurrency } from "../../utils/formatCurrency";
import { claseVariacion, flechaVariacion } from "./polaridad";
import { TOOLBAR_DESCARGA, LEYENDA } from "./graficos";
import { TITULO_CARD } from "./tituloCard";
// Los dos graficos comparan el mismo periodo contra el anterior: el color no
// distingue plata de tickets, distingue AHORA de ANTES.
import { ORIGEN, TIEMPO } from "../Stats2/theme";

const fmt = (v) => formatCurrency(v ?? 0, true);

// `metrica` decide el COLOR; la flecha siempre sigue al numero. Sin esto,
// "Salidas de caja +40%" y "Cancelados +100%" se felicitaban en verde.
const PctBadge = ({ value, metrica }) => {
  if (value == null || !isFinite(value)) return <span className="text-muted">-</span>;
  return (
    <span className={claseVariacion(value, metrica)} style={{ fontWeight: 600 }}>
      <span className="material-symbols-rounded" style={{ fontSize: 13, verticalAlign: "middle" }}>
        {flechaVariacion(value)}
      </span>
      {Math.abs(value).toFixed(1)}%
    </span>
  );
};

const pct = (curr, prev) => {
  if (!prev || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
};

const sum = (arr, key) => arr.reduce((a, d) => a + (d[key] || 0), 0);
const sumNested = (arr, path) =>
  arr.reduce((a, d) => {
    const parts = path.split(".");
    let val = d;
    for (const p of parts) val = val?.[p];
    return a + (val || 0);
  }, 0);

const buildPeriodData = (docs) => ({
  income: sum(docs, "income"),
  expense: sum(docs, "expense"),
  net: sum(docs, "net"),
  tickets_in: sumNested(docs, "tickets.total.in"),
  tickets_out: sumNested(docs, "tickets.total.out"),
  canceled: sum(docs, "canceled_count"),
  days: docs.length,
});

// ── Period calculators ──────────────────────────────────────

// El documento daily_{hoy} NUNCA se escribe: el desktop publica el dia anterior
// al cambiar de dia (StatsProcessor._onDayChange) y el resync filtra temporal=0.
// Por eso esta comparacion degradaba en silencio a "Ayer vs Anteayer". El dato
// de hoy si existe, pero viaja en el documento `live`.
const calcDaily = (dailyDocs, liveData) => {
  const today = moment().format("YYYY-MM-DD");
  const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
  const dayBefore = moment().subtract(2, "day").format("YYYY-MM-DD");

  const liveToday = liveData?.today;
  const todayDoc =
    dailyDocs.find((d) => d.date === today) ||
    (liveToday
      ? {
          date: today,
          income: liveToday.income || 0,
          expense: liveToday.expense || 0,
          net: liveToday.net || 0,
          canceled_count: liveToday.canceled || 0,
          tickets: {
            total: {
              in: liveToday.tickets_in || 0,
              out: liveToday.tickets_out || 0,
            },
          },
        }
      : undefined);
  const yesterdayDoc = dailyDocs.find((d) => d.date === yesterday);
  const dayBeforeDoc = dailyDocs.find((d) => d.date === dayBefore);

  const current = todayDoc || yesterdayDoc;
  const previous = todayDoc ? yesterdayDoc : dayBeforeDoc;

  if (!current && !previous) return null;

  const curr = current ? buildPeriodData([current]) : buildPeriodData([]);
  const prev = previous ? buildPeriodData([previous]) : buildPeriodData([]);

  const currentLabel = todayDoc ? "Hoy" : "Ayer";
  const previousLabel = todayDoc ? "Ayer" : "Anteayer";

  return {
    current: curr,
    previous: prev,
    currentLabel,
    previousLabel,
    incomePct: pct(curr.income, prev.income),
    ticketsPct: pct(curr.tickets_in, prev.tickets_in),
    footer: todayDoc
      ? `${moment().format("dddd DD/MM")} vs ${moment().subtract(1, "day").format("dddd DD/MM")}`
      : `${moment().subtract(1, "day").format("dddd DD/MM")} vs ${moment().subtract(2, "day").format("dddd DD/MM")}`,
  };
};

const calcWeekly = (dailyDocs) => {
  const today = moment();
  const startOfThisWeek = today.clone().startOf("isoWeek");
  const startOfLastWeek = startOfThisWeek.clone().subtract(7, "days");
  const endOfLastWeek = startOfThisWeek.clone().subtract(1, "day");
  const daysElapsed = today.isoWeekday();

  const tw = dailyDocs.filter((d) => {
    const m = moment(d.date);
    return m.isSameOrAfter(startOfThisWeek, "day") && m.isSameOrBefore(today, "day");
  });

  const lw = dailyDocs.filter((d) => {
    const m = moment(d.date);
    return m.isSameOrAfter(startOfLastWeek, "day") && m.isSameOrBefore(endOfLastWeek, "day");
  });

  const lwSamePeriod = dailyDocs.filter((d) => {
    const m = moment(d.date);
    const endComparable = startOfLastWeek.clone().add(daysElapsed - 1, "days");
    return m.isSameOrAfter(startOfLastWeek, "day") && m.isSameOrBefore(endComparable, "day");
  });

  if (!tw.length && !lw.length) return null;

  const curr = buildPeriodData(tw);
  const prev = buildPeriodData(lw);
  const prevSame = buildPeriodData(lwSamePeriod);
  const diaCorto = today.format("ddd");

  // La COLUMNA y el PORCENTAJE tienen que hablar del mismo periodo.
  //
  // Antes la columna mostraba la semana anterior COMPLETA y el porcentaje
  // comparaba contra los MISMOS DIAS transcurridos. Dos bases distintas en la
  // misma tabla: quien sacaba la cuenta a mano no llegaba nunca al numero que
  // le mostrabamos, y con razon.
  //
  // Gana el mismo periodo, porque es la comparacion justa: un martes al
  // mediodia no puede medirse contra una semana entera. La semana anterior
  // completa no se pierde, se dice al pie.
  return {
    current: curr,
    previous: prevSame,
    currentLabel: "Esta semana",
    previousLabel: `Lun a ${diaCorto}, semana pasada`,
    incomePct: pct(curr.income, prevSame.income),
    ticketsPct: pct(curr.tickets_in, prevSame.tickets_in),
    footer: `Lunes a ${diaCorto} contra los mismos días de la semana pasada.`
      + (prev.days ? ` La semana anterior completa dio ${fmt(prev.income)} en ${prev.days} días.` : ""),
  };
};

const calcMonthly = (dailyDocs, monthlyDocs) => {
  const today = moment();
  const thisMonthKey = today.format("YYYY-MM");
  const lastMonthKey = today.clone().subtract(1, "month").format("YYYY-MM");

  const thisMonthDays = dailyDocs.filter((d) => d.date?.startsWith(thisMonthKey));
  const curr = buildPeriodData(thisMonthDays);

  const lastMonthDoc = monthlyDocs.find((m) => m.month === lastMonthKey);

  if (!thisMonthDays.length && !lastMonthDoc) return null;

  const daysElapsed = today.date();
  const lastMonthSameDays = dailyDocs.filter((d) => {
    if (!d.date?.startsWith(lastMonthKey)) return false;
    const day = moment(d.date).date();
    return day <= daysElapsed;
  });
  const prevSame = buildPeriodData(lastMonthSameDays);

  // Use same-period data for fair comparison in both table and %
  const prev = prevSame.days > 0
    ? prevSame
    : lastMonthDoc
      ? {
          income: lastMonthDoc.income || 0,
          expense: lastMonthDoc.expense || 0,
          net: lastMonthDoc.net || 0,
          tickets_in: lastMonthDoc.tickets?.total?.in || 0,
          tickets_out: lastMonthDoc.tickets?.total?.out || 0,
          canceled: lastMonthDoc.canceled_count || 0,
          days: lastMonthDoc.days || 0,   // el emisor manda `days` (StatsProcessor.py:484)
        }
      : buildPeriodData([]);

  const hasSamePeriod = prevSame.days > 0;

  return {
    current: curr,
    previous: prev,
    currentLabel: today.format("MMMM"),
    previousLabel: hasSamePeriod
      ? `${today.clone().subtract(1, "month").format("MMM")} (1-${daysElapsed})`
      : today.clone().subtract(1, "month").format("MMMM"),
    incomePct: pct(curr.income, prev.income),
    ticketsPct: pct(curr.tickets_in, prev.tickets_in),
    footer: hasSamePeriod
      ? `Días 1-${daysElapsed} de ${today.format("MMM")} vs los mismos días de ${today.clone().subtract(1, "month").format("MMM")}`
      : `${today.format("MMMM")} (parcial) vs ${today.clone().subtract(1, "month").format("MMMM")} (completo)`,
  };
};

const calcYearly = (monthlyDocs) => {
  const today = moment();
  const thisYear = today.year();
  const lastYear = thisYear - 1;

  const thisYearMonths = monthlyDocs.filter((m) => m.month?.startsWith(String(thisYear)));
  const lastYearMonths = monthlyDocs.filter((m) => m.month?.startsWith(String(lastYear)));

  if (!thisYearMonths.length && !lastYearMonths.length) return null;

  const currentMonth = today.month();

  const lastYearSamePeriod = lastYearMonths.filter((m) => {
    const monthNum = parseInt(m.month.split("-")[1]) - 1;
    return monthNum <= currentMonth;
  });

  const sumMonthly = (arr, key) => arr.reduce((a, m) => a + (m[key] || 0), 0);
  const sumMonthlyNested = (arr, path) =>
    arr.reduce((a, m) => {
      const parts = path.split(".");
      let val = m;
      for (const p of parts) val = val?.[p];
      return a + (val || 0);
    }, 0);

  const curr = {
    income: sumMonthly(thisYearMonths, "income"),
    expense: sumMonthly(thisYearMonths, "expense"),
    net: sumMonthly(thisYearMonths, "net"),
    tickets_in: sumMonthlyNested(thisYearMonths, "tickets.total.in"),
    tickets_out: sumMonthlyNested(thisYearMonths, "tickets.total.out"),
    canceled: sumMonthly(thisYearMonths, "canceled_count"),
    days: thisYearMonths.length,
  };

  const prev = {
    income: sumMonthly(lastYearSamePeriod, "income"),
    expense: sumMonthly(lastYearSamePeriod, "expense"),
    net: sumMonthly(lastYearSamePeriod, "net"),
    tickets_in: sumMonthlyNested(lastYearSamePeriod, "tickets.total.in"),
    tickets_out: sumMonthlyNested(lastYearSamePeriod, "tickets.total.out"),
    canceled: sumMonthly(lastYearSamePeriod, "canceled_count"),
    days: lastYearSamePeriod.length,
  };

  return {
    current: curr,
    previous: prev,
    currentLabel: String(thisYear),
    previousLabel: String(lastYear),
    incomePct: pct(curr.income, prev.income),
    ticketsPct: pct(curr.tickets_in, prev.tickets_in),
    footer: lastYearSamePeriod.length > 0
      ? `Ene-${today.format("MMM")} ${thisYear} vs Ene-${today.format("MMM")} ${lastYear}`
      : `${thisYear} acumulado`,
  };
};

// ── Chart ───────────────────────────────────────────────────

const makeBarOptions = (categories, tooltipFormatter, yFormatter) => ({
  chart: {
    type: "bar",
    height: 180,
    ...TOOLBAR_DESCARGA,
    fontFamily: "inherit",
    animations: { dynamicAnimation: { enabled: false } },
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: "50%",
      borderRadius: 4,
      borderRadiusApplication: "end",
    },
  },
  // El periodo anterior nunca lleva color: va en el gris de TIEMPO y detras.
  // Sin esta regla "este periodo" terminaba verde en un grafico y azul en el de
  // al lado, pegados y en la misma pantalla.
  colors: [ORIGEN.tickets.color, TIEMPO.anterior.color],
  dataLabels: { enabled: false },
  stroke: { show: true, width: 2, colors: ["transparent"] },
  xaxis: {
    categories,
    labels: { style: { fontSize: "11px" } },
  },
  yaxis: {
    labels: {
      formatter: yFormatter,
      style: { fontSize: "10px" },
    },
  },
  tooltip: { y: { formatter: tooltipFormatter } },
  // Dos series pegadas —este período y el anterior— que sólo se distinguen por
  // el color: sin leyenda no hay forma de saber cuál barra es cuál.
  // El `markers: { size, shape }` que había acá es sintaxis de ApexCharts 4 y
  // el proyecto está en la 3.30: se ignoraba en silencio.
  legend: LEYENDA,
  grid: { borderColor: "#f1f1f1" },
});

const ComparisonCharts = ({ data }) => {
  if (!data) return null;

  const { current: curr, previous: prev, currentLabel, previousLabel } = data;
  const hasExpenses = curr.expense > 0 || prev.expense > 0;

  // Money chart - only show Ingresos if no expenses
  const moneyCategories = hasExpenses ? ["Ingresos", "Salidas de caja", "Neto"] : ["Ingresos"];
  const moneyCurrData = hasExpenses ? [curr.income, curr.expense, curr.net] : [curr.income];
  const moneyPrevData = hasExpenses ? [prev.income, prev.expense, prev.net] : [prev.income];

  const moneyOptions = makeBarOptions(
    moneyCategories,
    (v) => formatCurrency(v, true),
    (v) => formatCurrency(v),
  );

  const moneySeries = [
    { name: currentLabel, data: moneyCurrData },
    { name: previousLabel, data: moneyPrevData },
  ];

  // Tickets chart
  //
  // Las mismas palabras que usa el gráfico de la pestaña Tickets ("Cuántos autos
  // entraron y salieron cada día"). "Salidas" a secas quedaba a diez centímetros
  // de la categoría "Salidas de caja" del gráfico de al lado, que son egresos de
  // plata y no autos; y "Cancelados" no es la palabra que usa el resto de la
  // pantalla, que dice "anulados".
  const ticketsOptions = makeBarOptions(
    ["Autos que entraron", "Autos que salieron", "Tickets anulados"],
    (v) => Math.round(v).toLocaleString(),
    (v) => Math.round(v).toLocaleString(),
  );

  const ticketsSeries = [
    { name: currentLabel, data: [curr.tickets_in, curr.tickets_out, curr.canceled] },
    { name: previousLabel, data: [prev.tickets_in, prev.tickets_out, prev.canceled] },
  ];

  return (
    <Row>
      <Col md={6}>
        <h6 className="mb-1" style={{ ...TITULO_CARD, fontSize: 13 }}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>attach_money</span>
          La plata
        </h6>
        <ReactApexChart options={moneyOptions} series={moneySeries} type="bar" height={200} />
      </Col>
      <Col md={6}>
        <h6 className="mb-1" style={{ ...TITULO_CARD, fontSize: 13 }}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>confirmation_number</span>
          Los autos
        </h6>
        <ReactApexChart options={ticketsOptions} series={ticketsSeries} type="bar" height={200} />
      </Col>
    </Row>
  );
};

// ── Component ───────────────────────────────────────────────

const PERIODS = [
  { id: "day", label: "Día", icon: "today" },
  { id: "week", label: "Semana", icon: "date_range" },
  { id: "month", label: "Mes", icon: "calendar_month" },
  { id: "year", label: "Año", icon: "calendar_today" },
];

const ComparisonTable = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
        Todavía no hay datos suficientes para esta comparación
      </div>
    );
  }

  const { current: curr, previous: prev, currentLabel, previousLabel, incomePct, ticketsPct, footer } = data;
  const hasExpenses = curr.expense > 0 || prev.expense > 0;

  return (
    <>
      <Table bordered size="sm" responsive className="mb-2 align-middle">
        <thead>
          <tr className="table-light">
            <th></th>
            <th className="text-end">{currentLabel}</th>
            <th className="text-end">{previousLabel}</th>
            <th className="text-end">Variación</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="text-muted">Ingresos</td>
            <td className="text-end fw-bold text-success">{fmt(curr.income)}</td>
            <td className="text-end">{fmt(prev.income)}</td>
            <td className="text-end"><PctBadge value={incomePct} /></td>
          </tr>
          {hasExpenses && (
            <>
              <tr>
                <td className="text-muted">Salidas de caja</td>
                <td className="text-end text-danger">{fmt(curr.expense)}</td>
                <td className="text-end">{fmt(prev.expense)}</td>
                <td className="text-end"><PctBadge value={pct(curr.expense, prev.expense)} metrica="expense" /></td>
              </tr>
              <tr>
                <td className="text-muted">Neto</td>
                <td className="text-end fw-bold">{fmt(curr.net)}</td>
                <td className="text-end">{fmt(prev.net)}</td>
                <td className="text-end"><PctBadge value={pct(curr.net, prev.net)} /></td>
              </tr>
            </>
          )}
          {/* Las mismas palabras que el gráfico de arriba y que la pestaña
              Tickets. La fila de al lado dice "Salidas de caja" y son pesos:
              "Salidas" a secas, pegada, se leía como la misma cosa. */}
          <tr>
            <td className="text-muted">Autos que entraron</td>
            <td className="text-end fw-bold">{curr.tickets_in}</td>
            <td className="text-end">{prev.tickets_in}</td>
            <td className="text-end"><PctBadge value={ticketsPct} /></td>
          </tr>
          <tr>
            <td className="text-muted">Autos que salieron</td>
            <td className="text-end">{curr.tickets_out}</td>
            <td className="text-end">{prev.tickets_out}</td>
            <td className="text-end"><PctBadge value={pct(curr.tickets_out, prev.tickets_out)} /></td>
          </tr>
          <tr>
            <td className="text-muted">Tickets anulados</td>
            <td className="text-end text-warning">{curr.canceled}</td>
            <td className="text-end">{prev.canceled}</td>
            <td className="text-end"><PctBadge value={pct(curr.canceled, prev.canceled)} metrica="canceled" /></td>
          </tr>
        </tbody>
      </Table>
      <div className="text-muted text-center" style={{ fontSize: 11 }}>
        {footer}
      </div>
    </>
  );
};

const PeriodComparison = ({ dailyDocs, monthlyDocs = [], liveData = null }) => {
  const [activePeriod, setActivePeriod] = useState("week");

  // `liveData` VA en las dependencias: calcDaily lo usa para armar la columna
  // "Hoy". Sin el, esa columna quedaba congelada en el primer valor que llego
  // y no se movia en toda la sesion, aunque el resto de la pantalla si se
  // actualizara — que es la peor forma de estar mal: parecer fresco.
  const periods = useMemo(() => ({
    day: calcDaily(dailyDocs, liveData),
    week: calcWeekly(dailyDocs),
    month: calcMonthly(dailyDocs, monthlyDocs),
    year: calcYearly(monthlyDocs),
  }), [dailyDocs, monthlyDocs, liveData]);

  const availablePeriods = PERIODS.filter((p) => periods[p.id] !== null);

  if (!availablePeriods.length) return null;

  const effectivePeriod = periods[activePeriod] ? activePeriod : availablePeriods[0]?.id;
  const activeData = periods[effectivePeriod];

  return (
    <Card className="shadow-sm">
      <CardHeader className="bg-white py-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h6 className="mb-0" style={TITULO_CARD}>
            <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>
              compare_arrows
            </span>
            Cómo vas contra el período anterior
          </h6>
          <Nav pills className="gap-1">
            {PERIODS.map((p) => {
              const hasData = periods[p.id] !== null;
              return (
                <NavItem key={p.id}>
                  <NavLink
                    className={`py-1 px-2 ${effectivePeriod === p.id ? "active" : ""}`}
                    style={{
                      cursor: hasData ? "pointer" : "default",
                      fontSize: 12,
                      opacity: hasData ? 1 : 0.4,
                    }}
                    onClick={() => hasData && setActivePeriod(p.id)}
                    disabled={!hasData}
                  >
                    <span
                      className="material-symbols-rounded me-1 d-none d-sm-inline"
                      style={{ fontSize: 13, verticalAlign: "middle" }}
                    >
                      {p.icon}
                    </span>
                    {p.label}
                  </NavLink>
                </NavItem>
              );
            })}
          </Nav>
        </div>
      </CardHeader>
      <CardBody>
        <Row>
          <Col lg={5}>
            <ComparisonTable data={activeData} />
          </Col>
          <Col lg={7}>
            <ComparisonCharts data={activeData} />
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default PeriodComparison;
